import { Decimal } from 'decimal.js';
import { compact, first, orderBy } from 'lodash';
import { useMemo } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { EstateWaterfallTransferKind } from '@/types/schema';
import { sumDecimalJS } from '@/utils/decimalJSUtils';
import { getNodes } from '@/utils/graphqlUtils';

import {
  GetHypotheticalTransfers_EstateWaterfallFragment,
  GetHypotheticalTransfers_EstateWaterfallLoanFragment,
  GetHypotheticalTransfers_HypotheticalSaleLoanFragment,
  GetHypotheticalTransfers_HypotheticalTransferFragment,
  useGetHypotheticalTransfersQuery,
} from '../graphql/GetHypotheticalTransfers.generated';

export interface SaleLoanWithProjections {
  saleLoan: GetHypotheticalTransfers_HypotheticalSaleLoanFragment;
  loanProjections: GetHypotheticalTransfers_EstateWaterfallLoanFragment | null;
}

export interface TransferWithValue {
  transfer: GetHypotheticalTransfers_HypotheticalTransferFragment;
  value: Decimal;
}

interface GetHypotheticalTransfersInput {
  transferReferenceObjId?: string;
  waterfallId: string;
  isIndividual: boolean;
  isEntity: boolean;
  isProposal: boolean;
}

export function useGetHypotheticalTransfers({
  transferReferenceObjId,
  waterfallId,
  isIndividual,
  isEntity,
  isProposal,
}: GetHypotheticalTransfersInput) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const {
    inboundHypotheticalTransfersWhere,
    outboundHypotheticalTransfersWhere,
    skipInboundHypotheticalTransfers,
    skipOutboundHypotheticalTransfers,
  } = useMemo(() => {
    const outboundHypotheticalTransfersWhere = (() => {
      if (isEntity) {
        return {
          hasSourceEntityWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      if (isIndividual) {
        return {
          hasSourceIndividualWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      return;
    })();

    const inboundHypotheticalTransfersWhere = (() => {
      if (isEntity) {
        return {
          hasDestinationEntityWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      if (isIndividual) {
        return {
          hasDestinationIndividualWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      return;
    })();

    return {
      inboundHypotheticalTransfersWhere:
        inboundHypotheticalTransfersWhere ?? {},
      outboundHypotheticalTransfersWhere:
        outboundHypotheticalTransfersWhere ?? {},
      skipInboundHypotheticalTransfers: !inboundHypotheticalTransfersWhere,
      skipOutboundHypotheticalTransfers: !outboundHypotheticalTransfersWhere,
    };
  }, [isEntity, isIndividual, transferReferenceObjId]);

  const {
    inboundHypotheticalSaleLoansWhere,
    outboundHypotheticalSaleLoansWhere,
    skipInboundHypotheticalSaleLoans,
    skipOutboundHypotheticalSaleLoans,
  } = useMemo(() => {
    const inboundHypotheticalSaleLoansWhere = (() => {
      if (isEntity) {
        return {
          hasSourceEntityWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      if (isIndividual) {
        return {
          hasSourceIndividualWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      return;
    })();

    const outboundHypotheticalSaleLoansWhere = (() => {
      if (isEntity) {
        return {
          hasRecipientEntityWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      if (isIndividual) {
        return {
          hasRecipientIndividualWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      return;
    })();

    return {
      inboundHypotheticalSaleLoansWhere:
        inboundHypotheticalSaleLoansWhere ?? {},
      outboundHypotheticalSaleLoansWhere:
        outboundHypotheticalSaleLoansWhere ?? {},
      skipInboundHypotheticalSaleLoans: !inboundHypotheticalSaleLoansWhere,
      skipOutboundHypotheticalSaleLoans: !outboundHypotheticalSaleLoansWhere,
    };
  }, [isEntity, isIndividual, transferReferenceObjId]);

  const {
    inboundHypotheticalGratsWhere,
    outboundHypotheticalGratsWhere,
    skipInboundHypotheticalGrats,
    skipOutboundHypotheticalGrats,
  } = useMemo(() => {
    const inboundHypotheticalGratsWhere = (() => {
      if (isEntity) {
        return {
          hasSourceEntityWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      if (isIndividual) {
        return {
          hasSourceIndividualWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      return;
    })();

    const outboundHypotheticalGratsWhere = (() => {
      if (isEntity) {
        return {
          hasSimulatedGratWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      return;
    })();

    return {
      inboundHypotheticalGratsWhere: inboundHypotheticalGratsWhere ?? {},
      outboundHypotheticalGratsWhere: outboundHypotheticalGratsWhere ?? {},
      skipInboundHypotheticalGrats: !inboundHypotheticalGratsWhere,
      skipOutboundHypotheticalGrats: !outboundHypotheticalGratsWhere,
    };
  }, [isEntity, isIndividual, transferReferenceObjId]);

  const {
    inboundHypotheticalCrtsWhere,
    outboundHypotheticalCrtsWhere,
    skipInboundHypotheticalCrts,
    skipOutboundHypotheticalCrts,
  } = useMemo(() => {
    const outboundHypotheticalCrtsWhere = (() => {
      if (isEntity) {
        return {
          hasSourceEntityWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      if (isIndividual) {
        return {
          hasSourceIndividualWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      return;
    })();

    const inboundHypotheticalCrtsWhere = (() => {
      if (isProposal) {
        return {
          hasSimulatedCrtWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      return;
    })();

    return {
      inboundHypotheticalCrtsWhere: inboundHypotheticalCrtsWhere ?? {},
      outboundHypotheticalCrtsWhere: outboundHypotheticalCrtsWhere ?? {},
      skipInboundHypotheticalCrts: !inboundHypotheticalCrtsWhere,
      skipOutboundHypotheticalCrts: !outboundHypotheticalCrtsWhere,
    };
  }, [isEntity, isIndividual, isProposal, transferReferenceObjId]);

  const {
    inboundHypotheticalCltsWhere,
    outboundHypotheticalCltsWhere,
    skipInboundHypotheticalClts,
    skipOutboundHypotheticalClts,
  } = useMemo(() => {
    const outboundHypotheticalCltsWhere = (() => {
      if (isEntity) {
        return {
          hasSourceEntityWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      if (isIndividual) {
        return {
          hasSourceIndividualWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      return;
    })();

    const inboundHypotheticalCltsWhere = (() => {
      if (isProposal) {
        return {
          hasSimulatedCltWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      return;
    })();

    return {
      inboundHypotheticalCltsWhere: inboundHypotheticalCltsWhere ?? {},
      outboundHypotheticalCltsWhere: outboundHypotheticalCltsWhere ?? {},
      skipInboundHypotheticalClts: !inboundHypotheticalCltsWhere,
      skipOutboundHypotheticalClts: !outboundHypotheticalCltsWhere,
    };
  }, [isEntity, isIndividual, isProposal, transferReferenceObjId]);

  const {
    inboundHypotheticalInsuranceWhere,
    outboundHypotheticalInsuranceWhere,
    skipInboundHypotheticalInsurance,
    skipOutboundHypotheticalInsurance,
  } = useMemo(() => {
    const outboundHypotheticalInsuranceWhere = (() => {
      if (isEntity) {
        return {
          hasSourceEntityWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      if (isIndividual) {
        return {
          hasSourceIndividualWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      return;
    })();

    const inboundHypotheticalInsuranceWhere = (() => {
      if (isEntity) {
        return {
          hasDestinationEntityWith: [
            {
              id: transferReferenceObjId,
            },
          ],
        };
      }

      return;
    })();

    return {
      inboundHypotheticalInsuranceWhere:
        inboundHypotheticalInsuranceWhere ?? {},
      outboundHypotheticalInsuranceWhere:
        outboundHypotheticalInsuranceWhere ?? {},
      skipInboundHypotheticalInsurance: !inboundHypotheticalInsuranceWhere,
      skipOutboundHypotheticalInsurance: !outboundHypotheticalInsuranceWhere,
    };
  }, [isEntity, isIndividual, transferReferenceObjId]);

  const queryProps = useGetHypotheticalTransfersQuery({
    variables: {
      where: {
        id: waterfallId,
      },
      outboundHypotheticalTransfersWhere,
      inboundHypotheticalTransfersWhere,
      skipOutboundHypotheticalTransfers,
      skipInboundHypotheticalTransfers,
      outboundHypotheticalSaleLoansWhere,
      inboundHypotheticalSaleLoansWhere,
      outboundHypotheticalGratsWhere,
      skipInboundHypotheticalSaleLoans,
      skipOutboundHypotheticalSaleLoans,
      inboundHypotheticalGratsWhere,
      skipInboundHypotheticalGrats,
      skipOutboundHypotheticalGrats,
      outboundHypotheticalCrtsWhere,
      inboundHypotheticalCrtsWhere,
      skipInboundHypotheticalCrts,
      skipOutboundHypotheticalCrts,
      outboundHypotheticalCltsWhere,
      inboundHypotheticalCltsWhere,
      skipInboundHypotheticalClts,
      skipOutboundHypotheticalClts,
      outboundHypotheticalInsuranceWhere,
      inboundHypotheticalInsuranceWhere,
      skipOutboundHypotheticalInsurance,
      skipInboundHypotheticalInsurance,
    },
    fetchPolicy: 'cache-and-network', // Make sure transfers are represented in the cache
    onError: (err) => {
      showFeedback(
        'Failed to fetch transfers. Please refresh the page and try again.'
      );
      reportError('Failed to fetch transfers', err);
    },
    skip: !transferReferenceObjId,
  });

  const { data } = queryProps;

  const {
    inboundTransfers,
    outboundTransfers,
  }: {
    inboundTransfers: TransferWithValue[];
    outboundTransfers: TransferWithValue[];
  } = useMemo(() => {
    const waterfall = getNodes(data?.estateWaterfalls)[0];

    if (!waterfall) {
      return {
        inboundTransfers: [],
        outboundTransfers: [],
      };
    }

    const getTransfers = (
      waterfall: GetHypotheticalTransfers_EstateWaterfallFragment,
      direction: 'inbound' | 'outbound'
    ) => {
      const transfers =
        direction === 'inbound'
          ? waterfall.inboundTransfers
          : waterfall.outboundTransfers;

      const orderedTransfers = getNodes(transfers).sort((a, b) => {
        return a.transferOrder - b.transferOrder;
      });

      return orderedTransfers.map((t): TransferWithValue => {
        const associatedTransferLogs = compact(
          waterfall.visualizationWithProjections?.transferLog?.filter(
            (a) => a?.associatedObjectID === t.id
          )
        );

        if (!associatedTransferLogs) {
          return {
            transfer: t,
            value: new Decimal(0),
          };
        }

        return {
          transfer: t,
          value: sumDecimalJS(
            associatedTransferLogs.map(
              (a) => a.finalTransferAmount ?? new Decimal(0)
            )
          ),
        };
      });
    };

    return {
      inboundTransfers: getTransfers(waterfall, 'inbound'),
      outboundTransfers: getTransfers(waterfall, 'outbound'),
    };
  }, [data?.estateWaterfalls]);

  const {
    sourceLoans,
    recipientLoans,
  }: {
    sourceLoans: SaleLoanWithProjections[];
    recipientLoans: SaleLoanWithProjections[];
  } = useMemo(() => {
    const waterfall = first(getNodes(data?.estateWaterfalls));

    if (!waterfall) {
      return {
        sourceLoans: [],
        recipientLoans: [],
      };
    }

    const getSaleLoans = (
      waterfall: GetHypotheticalTransfers_EstateWaterfallFragment,
      direction: 'inbound' | 'outbound'
    ) => {
      const saleLoans =
        direction === 'inbound'
          ? waterfall.inboundHypotheticalSaleLoans
          : waterfall.outboundHypotheticalSaleLoans;

      return (
        getNodes(saleLoans).map((hsl) => {
          // we want to expose both the sale/loan object itself and the projections that are
          // associated with it
          return {
            saleLoan: hsl,
            loanProjections:
              waterfall?.visualizationWithProjections?.loans?.find(
                (l) => l.id === hsl.id
              ) ?? null,
          };
        }) ?? []
      );
    };

    return {
      sourceLoans: getSaleLoans(waterfall, 'inbound'),
      recipientLoans: getSaleLoans(waterfall, 'outbound'),
    };
  }, [data?.estateWaterfalls]);

  const { sourceHypotheticalGrats, recipientHypotheticalGrats } =
    useMemo(() => {
      const waterfall = first(getNodes(data?.estateWaterfalls));

      if (!waterfall) {
        return {
          sourceHypotheticalGrats: [],
          recipientHypotheticalGrats: [],
        };
      }

      return {
        sourceHypotheticalGrats: getNodes(waterfall.inboundHypotheticalGrats),
        recipientHypotheticalGrats: getNodes(
          waterfall.outboundHypotheticalGrats
        ),
      };
    }, [data?.estateWaterfalls]);

  const {
    inboundGratAnnuities,
    outboundGratAnnuities,
    inboundGratBeneficiaryPayments,
    outboundGratBeneficiaryPayments,
  } = useMemo(() => {
    const waterfall = first(getNodes(data?.estateWaterfalls));
    const transferLog = waterfall?.visualizationWithProjections?.transferLog;

    if (!transferLog) {
      return {
        inboundGratAnnuities: [],
        outboundGratAnnuities: [],
        inboundGratBeneficiaryPayments: [],
        outboundGratBeneficiaryPayments: [],
      };
    }

    const inboundTransfers = orderBy(
      transferLog.filter((tl) => tl.destination?.id === transferReferenceObjId),
      'year'
    );

    const inboundGratAnnuities = inboundTransfers.filter(
      (gt) => gt.kind === EstateWaterfallTransferKind.GratAnnuityPayment
    );

    const inboundGratBeneficiaryPayments = inboundTransfers.filter(
      (gt) =>
        gt.kind === EstateWaterfallTransferKind.BeneficiaryPayment &&
        (gt.associatedObject?.__typename === 'HypotheticalGRAT' ||
          (gt.associatedObject?.__typename === 'Entity' &&
            gt.associatedObject?.subtype?.__typename === 'GRATTrust'))
    );

    const outboundTransfers = orderBy(
      transferLog.filter((tl) => tl.source?.id === transferReferenceObjId),
      'year'
    );

    const outboundGratAnnuities = outboundTransfers.filter(
      (gt) => gt.kind === EstateWaterfallTransferKind.GratAnnuityPayment
    );

    const outboundGratBeneficiaryPayments = outboundTransfers.filter(
      (gt) =>
        gt.kind === EstateWaterfallTransferKind.BeneficiaryPayment &&
        (gt.associatedObject?.__typename === 'HypotheticalGRAT' ||
          (gt.associatedObject?.__typename === 'Entity' &&
            gt.associatedObject?.subtype?.__typename === 'GRATTrust'))
    );

    return {
      inboundGratAnnuities,
      outboundGratAnnuities,
      inboundGratBeneficiaryPayments,
      outboundGratBeneficiaryPayments,
    };
  }, [data?.estateWaterfalls, transferReferenceObjId]);

  const { inboundQprtBeneficiaryPayments, outboundQprtBeneficiaryPayments } =
    useMemo(() => {
      const waterfall = first(getNodes(data?.estateWaterfalls));
      const transferLog = waterfall?.visualizationWithProjections?.transferLog;

      if (!transferLog) {
        return {
          inboundQprtBeneficiaryPayments: [],
          outboundQprtBeneficiaryPayments: [],
        };
      }

      const inboundTransfers = orderBy(
        transferLog.filter(
          (tl) => tl.destination?.id === transferReferenceObjId
        ),
        'year'
      );

      const inboundQprtBeneficiaryPayments = inboundTransfers.filter(
        (gt) => gt.kind === EstateWaterfallTransferKind.QprtBeneficiaryPayment
      );

      const outboundTransfers = orderBy(
        transferLog.filter((tl) => tl.source?.id === transferReferenceObjId),
        'year'
      );

      const outboundQprtBeneficiaryPayments = outboundTransfers.filter(
        (gt) => gt.kind === EstateWaterfallTransferKind.QprtBeneficiaryPayment
      );

      return {
        inboundQprtBeneficiaryPayments,
        outboundQprtBeneficiaryPayments,
      };
    }, [data?.estateWaterfalls, transferReferenceObjId]);

  const { sourceHypotheticalCrts, recipientHypotheticalCrts } = useMemo(() => {
    const waterfall = first(getNodes(data?.estateWaterfalls));

    if (!waterfall) {
      return {
        sourceHypotheticalCrts: [],
        recipientHypotheticalCrts: [],
      };
    }

    return {
      sourceHypotheticalCrts: getNodes(waterfall.outboundHypotheticalCrts),
      recipientHypotheticalCrts: getNodes(waterfall.inboundHypotheticalCrts),
    };
  }, [data?.estateWaterfalls]);

  const {
    inboundHypotheticalCrtIncomePayments,
    outboundHypotheticalCrtIncomePayments,
    inboundHypotheticalCrtBeneficiaryPayments,
    outboundHypotheticalCrtBeneficiaryPayments,
  } = useMemo(() => {
    const waterfall = first(getNodes(data?.estateWaterfalls));
    const transferLog = waterfall?.visualizationWithProjections?.transferLog;

    if (!transferLog) {
      return {
        inboundHypotheticalCrtIncomePayments: [],
        outboundHypotheticalCrtIncomePayments: [],
        inboundHypotheticalCrtBeneficiaryPayments: [],
        outboundHypotheticalCrtBeneficiaryPayments: [],
      };
    }

    const inboundTransfers = orderBy(
      transferLog.filter((tl) => tl.destination?.id === transferReferenceObjId),
      'year'
    );

    const inboundHypotheticalCrtIncomePayments = inboundTransfers.filter(
      (gt) => gt.kind === EstateWaterfallTransferKind.CrtIncomePayment
    );

    const inboundHypotheticalCrtBeneficiaryPayments = inboundTransfers.filter(
      (gt) => gt.kind === EstateWaterfallTransferKind.CrtCharitableRemainder
    );

    const outboundTransfers = orderBy(
      transferLog.filter((tl) => tl.source?.id === transferReferenceObjId),
      'year'
    );

    const outboundHypotheticalCrtIncomePayments = outboundTransfers.filter(
      (gt) => gt.kind === EstateWaterfallTransferKind.CrtIncomePayment
    );

    const outboundHypotheticalCrtBeneficiaryPayments = outboundTransfers.filter(
      (gt) => gt.kind === EstateWaterfallTransferKind.CrtCharitableRemainder
    );

    return {
      inboundHypotheticalCrtIncomePayments,
      outboundHypotheticalCrtIncomePayments,
      inboundHypotheticalCrtBeneficiaryPayments,
      outboundHypotheticalCrtBeneficiaryPayments,
    };
  }, [data?.estateWaterfalls, transferReferenceObjId]);

  const { sourceHypotheticalClts, recipientHypotheticalClts } = useMemo(() => {
    const waterfall = first(getNodes(data?.estateWaterfalls));

    if (!waterfall) {
      return {
        sourceHypotheticalClts: [],
        recipientHypotheticalClts: [],
      };
    }

    return {
      sourceHypotheticalClts: getNodes(waterfall.outboundHypotheticalClts),
      recipientHypotheticalClts: getNodes(waterfall.inboundHypotheticalClts),
    };
  }, [data?.estateWaterfalls]);

  const {
    inboundHypotheticalCltIncomePayments,
    inboundHypotheticalCltBeneficiaryPayments,
    outboundHypotheticalCltIncomePayments,
    outboundHypotheticalCltBeneficiaryPayments,
  } = useMemo(() => {
    const waterfall = first(getNodes(data?.estateWaterfalls));
    const transferLog = waterfall?.visualizationWithProjections?.transferLog;

    if (!transferLog) {
      return {
        inboundHypotheticalCltIncomePayments: [],
        inboundHypotheticalCltBeneficiaryPayments: [],
        outboundHypotheticalCltIncomePayments: [],
        outboundHypotheticalCltBeneficiaryPayments: [],
      };
    }

    const inboundTransfers = orderBy(
      transferLog.filter((tl) => tl.destination?.id === transferReferenceObjId),
      'year'
    );

    const inboundHypotheticalCltIncomePayments = inboundTransfers.filter(
      (gt) => gt.kind === EstateWaterfallTransferKind.CltIncomePayment
    );

    const inboundHypotheticalCltBeneficiaryPayments = inboundTransfers.filter(
      (gt) => gt.kind === EstateWaterfallTransferKind.CltBeneficiaryRemainder
    );

    const outboundTransfers = orderBy(
      transferLog.filter((tl) => tl.source?.id === transferReferenceObjId),
      'year'
    );

    const outboundHypotheticalCltIncomePayments = outboundTransfers.filter(
      (gt) => gt.kind === EstateWaterfallTransferKind.CltIncomePayment
    );

    const outboundHypotheticalCltBeneficiaryPayments = outboundTransfers.filter(
      (gt) => gt.kind === EstateWaterfallTransferKind.CltBeneficiaryRemainder
    );

    return {
      inboundHypotheticalCltIncomePayments,
      inboundHypotheticalCltBeneficiaryPayments,
      outboundHypotheticalCltIncomePayments,
      outboundHypotheticalCltBeneficiaryPayments,
    };
  }, [data?.estateWaterfalls, transferReferenceObjId]);

  const { sourceHypotheticalInsurance, recipientHypotheticalInsurance } =
    useMemo(() => {
      const waterfall = first(getNodes(data?.estateWaterfalls));

      if (!waterfall) {
        return {
          sourceHypotheticalInsurance: [],
          recipientHypotheticalInsurance: [],
        };
      }

      return {
        sourceHypotheticalInsurance: getNodes(
          waterfall.outboundHypotheticalInsurance
        ),
        recipientHypotheticalInsurance: getNodes(
          waterfall.inboundHypotheticalInsurance
        ),
      };
    }, [data?.estateWaterfalls]);

  // This is the value history of the reference object (the entity or individual that we're looking at the
  // This is the value history of the reference object (the entity or individual that we're looking at the
  // hypothetical transfers for). It's used to look up the value of the reference object after the hypothetical
  // transfers are applied.
  const referenceObjectValueHistory = useMemo(() => {
    const waterfall = first(getNodes(data?.estateWaterfalls));
    const valueHistory =
      waterfall?.visualizationWithProjections?.nodes?.find(
        (n) => n.id === transferReferenceObjId
      )?.valueHistory ?? null;

    if (!valueHistory) {
      return valueHistory;
    }
    return orderBy(valueHistory, 'year');
  }, [data?.estateWaterfalls, transferReferenceObjId]);

  const transferLog = useMemo(() => {
    const waterfall = getNodes(data?.estateWaterfalls)[0];

    if (!waterfall) {
      return [];
    }

    return waterfall.visualizationWithProjections?.transferLog ?? [];
  }, [data?.estateWaterfalls]);

  return {
    ...queryProps,
    // Transfers
    outboundTransfers,
    inboundTransfers,
    // Loans
    sourceLoans,
    recipientLoans,
    // Grats
    sourceHypotheticalGrats,
    recipientHypotheticalGrats,
    // Grats Annuities
    inboundGratAnnuities,
    outboundGratAnnuities,
    // Grats Beneficiary Payments
    inboundGratBeneficiaryPayments,
    outboundGratBeneficiaryPayments,
    // QPRT Beneficiary Payments
    inboundQprtBeneficiaryPayments,
    outboundQprtBeneficiaryPayments,
    // Crts
    sourceHypotheticalCrts,
    recipientHypotheticalCrts,
    // Crts Income Payments
    inboundHypotheticalCrtIncomePayments,
    outboundHypotheticalCrtIncomePayments,
    // Crts Beneficiary Payments
    inboundHypotheticalCrtBeneficiaryPayments,
    outboundHypotheticalCrtBeneficiaryPayments,
    // Clts
    sourceHypotheticalClts,
    recipientHypotheticalClts,
    // Clts Income Payments
    inboundHypotheticalCltIncomePayments,
    outboundHypotheticalCltIncomePayments,
    // Clts Beneficiary Payments
    inboundHypotheticalCltBeneficiaryPayments,
    outboundHypotheticalCltBeneficiaryPayments,
    // Insurance
    sourceHypotheticalInsurance,
    recipientHypotheticalInsurance,

    transferLog,
    referenceObjectValueHistory,
  };
}
